/**
 * THE ANCHOR CONTRACT.
 *
 * A tour step points at a real button on a real screen. The only thing it is allowed to point at it
 * BY is this attribute. Not a CSS class (renamed by whoever restyles the page), not the button's
 * text (changed by a copy edit), not "the third button in the header" (moved the first time someone
 * adds a fourth). `data-tour="daily-report.submit"` is a DECLARED CONTRACT between the component and
 * the tour, and like any contract the point is that breaking it is loud — see
 * __tests__/tourAnchors.contract.test.jsx, which mounts every screen a tour touches and fails by
 * name when an anchor stops existing.
 *
 * Adding an anchor to a component is one spread:
 *
 *   <button {...tourAnchor('daily-report.submit')} onClick={handleSubmit}>Submit</button>
 *
 * It renders as a plain attribute, changes nothing about the element, and costs nothing at runtime.
 */

export const TOUR_ANCHOR_ATTR = 'data-tour'

/**
 * Anchor names are `area.thing` or `area.thing.action`, lowercase, hyphens inside a segment.
 * Enforced (tourSchema.js) rather than merely suggested, because the namespace is shared across
 * modules and "submit" vs "daily-report.submit" is the difference between a unique target and a
 * collision that silently spotlights the wrong element on some other page.
 */
export const ANCHOR_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\.[a-z0-9]+(?:-[a-z0-9]+)*)+$/

/** Props to spread onto the element a step targets. */
export const tourAnchor = (name) => ({ [TOUR_ANCHOR_ATTR]: name })

/**
 * ══ THE SECOND HALF OF THE CONTRACT: "HAS THIS BEEN ANSWERED?" ══════════════════════════════════
 *
 * An anchor says WHERE a question is. These two say whether it has been ANSWERED, and by whom the
 * question is called — both declared by the component that owns the answer, because it is the only
 * thing that knows.
 *
 * WHY IT IS AN ATTRIBUTE AND NOT A CALLBACK, A CONTEXT OR A STORE. Same reason the anchor is: the
 * tour engine must be able to ask a screen a question without the screen importing the tour, and the
 * DOM is the one interface every screen already has. It also means the gate re-evaluates for free —
 * `data-tour-answered` flipping is a mutation, and gateDetectors already watches those.
 *
 * WHAT IT MUST NEVER MEAN is "the input has text in it". The whole point of publishing this from the
 * component is that the component reads its own ANSWER: a picker's search text, a half-typed name, a
 * table row with nothing in it — none of those are answers, and all of them look like one from
 * outside. See isAnswered in modules/forms/components/validate.js for the sheet that does this.
 *
 * ABSENCE IS NOT "NO". An element that is not on the page is a question that is not being asked
 * (a conditional follow-up whose trigger says otherwise), and the `answered` gate treats it that
 * way. An element that IS on the page and does not carry this attribute has not been taught to
 * answer, and a gate pointed at one will never be satisfied — deliberately loud rather than
 * silently true.
 */
export const TOUR_ANSWERED_ATTR = 'data-tour-answered'
/** The question's own words, so a card holding somebody can name what is still missing. */
export const TOUR_LABEL_ATTR = 'data-tour-label'

/** Props to spread beside `tourAnchor` on a control that holds an answer. */
export const tourAnswer = (answered, label) => ({
  [TOUR_ANSWERED_ATTR]: answered ? 'yes' : 'no',
  ...(label ? { [TOUR_LABEL_ATTR]: String(label) } : {}),
})

/**
 * CSS.escape is not in jsdom 22 and not in every mobile browser we support, and anchor names are
 * already restricted to `[a-z0-9.-]` by ANCHOR_NAME_PATTERN — none of which need escaping in an
 * attribute-value selector. Guard anyway so a malformed name can never build a selector that
 * matches something unintended.
 */
export const anchorSelector = (name) => {
  if (!ANCHOR_NAME_PATTERN.test(name)) return null
  return `[${TOUR_ANCHOR_ATTR}="${name}"]`
}

/**
 * Resolve an anchor within `root`.
 *
 * Returns the count as well as the element on purpose. TWO elements carrying one anchor is not a
 * harmless duplicate: the overlay would spotlight whichever the DOM happens to return first, which
 * is the "confidently highlights the wrong button" failure — strictly worse than highlighting
 * nothing, because nothing is obviously broken and the wrong button is not. Callers treat count > 1
 * as an error, not a warning.
 */
export const findAnchor = (name, root) => {
  const scope = root || (typeof document === 'undefined' ? null : document)
  const selector = anchorSelector(name)
  if (!scope || !selector) return { element: null, count: 0 }
  const matches = scope.querySelectorAll(selector)
  return { element: matches[0] || null, count: matches.length }
}
